import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

import { useQuery } from "@tanstack/react-query";
import { useClienteService } from "@/hooks/useUnifiedService";
import {
    Mail,
    Phone,
    User,
    Calendar,
    DollarSign,
    Target,
    FileText,
    Loader2,
    Paperclip,
    ExternalLink,
    File,
    MapPin,
    Briefcase,
    ShieldCheck,
    CreditCard,
    Home,
    TrendingUp,
    UserCircle,
    Building2,
    CalendarDays
} from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
    Tabs,
    Descriptions,
    Statistic,
    Card as AntCard,
    Tag,
    Tooltip as AntTooltip,
    Badge as AntBadge
} from 'antd';
import { STATUS_MAP } from "../constants";
import { formatStatusLabel } from "@/lib/statusLabels";
import { formatTempoProcuraLabel } from "@/lib/qualificacaoLabels";

const { TabPane } = Tabs;

const PRIORITY_LABELS: Record<string, string> = {
    ALTA: "Alta",
    MEDIA: "Média",
    BAIXA: "Baixa",
    APENAS_PESQUISANDO: "Apenas Pesquisando"
};

const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "N/A";
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numericValue)) return "N/A";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numericValue);
};

const formatCpfCnpj = (value: string | null | undefined) => {
    if (!value) return "N/A";
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    } else if (digits.length === 14) {
        return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    return value;
};

const formatPhone = (phone: string | null | undefined) => {
    if (!phone) return "N/A";
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
        return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    }
    if (cleaned.length === 10) {
        return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    }
    return phone;
};

const formatDate = (date: any) => {
    if (!date) return "N/A";
    try {
        const d = typeof date === 'string' ? parseISO(date) : new Date(date);
        return isValid(d) ? format(d, "dd/MM/yyyy", { locale: ptBR }) : "N/A";
    } catch {
        return "N/A";
    }
};

interface ClienteDetalhesModalProps {
    clienteId: number | string | null;
    isOpen: boolean;
    onClose: () => void;
}

export function ClienteDetalhesModal({ clienteId, isOpen, onClose }: ClienteDetalhesModalProps) {
    const { findById, listarDocumentos } = useClienteService();

    const { data: cliente, isLoading: isLoadingCliente } = useQuery({
        queryKey: ['cliente', clienteId],
        queryFn: () => findById(clienteId!.toString()),
        enabled: !!clienteId && isOpen
    });

    const { data: documentos, isLoading: isLoadingDocs } = useQuery({
        queryKey: ['cliente-documentos', clienteId],
        queryFn: () => listarDocumentos(clienteId!.toString()),
        enabled: !!clienteId && isOpen
    });

    const isLoading = isLoadingCliente || isLoadingDocs;

    const getStatusColor = (status: string) => {
        const s = String(status).toLowerCase();
        if (['ativo', 'contemplado'].includes(s)) return 'success';
        if (['pending', 'draft'].includes(s)) return 'processing';
        return 'default';
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto p-0 border-none bg-[#f8fafc] dark:bg-zinc-950">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center p-24">
                        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                        <p className="text-muted-foreground animate-pulse">Carregando perfil do cliente...</p>
                    </div>
                ) : !cliente ? (
                    <div className="p-24 text-center">
                        <UserCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-20" />
                        <h3 className="text-xl font-bold">Cliente não localizado</h3>
                        <p className="text-muted-foreground">O registro solicitado não foi encontrado ou você não tem permissão para visualizá-lo.</p>
                    </div>
                ) : (
                    <div className="flex flex-col h-full">
                        {/* ── HEADER ── */}
                        <div className="bg-white dark:bg-zinc-900 border-b border-border/60 p-6 sticky top-0 z-10 shadow-sm">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="bg-primary/10 p-4 rounded-2xl">
                                        <User className="h-8 w-8 text-primary" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">Perfil do Cliente</span>
                                            {cliente.status && (
                                                <Tag color={getStatusColor(cliente.status)} className="rounded-full px-3 font-bold m-0 border-none uppercase text-[10px]">
                                                    {STATUS_MAP[cliente.status as keyof typeof STATUS_MAP]?.label || formatStatusLabel(cliente.status)}
                                                </Tag>
                                            )}
                                        </div>
                                        <DialogTitle className="text-2xl font-black text-foreground tracking-tight">
                                            {cliente.nome}
                                        </DialogTitle>
                                        <DialogDescription className="sr-only">
                                            Detalhes e histórico completo do cliente {cliente.nome}
                                        </DialogDescription>

                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <AntTooltip title="Data de Cadastro">
                                        <div className="flex items-center gap-2 px-4 py-2 bg-muted/40 rounded-xl text-xs font-semibold">
                                            <CalendarDays className="h-4 w-4 text-muted-foreground" />
                                            {formatDate(cliente.created_at)}
                                        </div>
                                    </AntTooltip>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* ── INSIGHTS ROW ── */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <AntCard className="shadow-sm border-none bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden hover:ring-1 hover:ring-primary/10 transition-all">
                                    <Statistic
                                        title={<span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">Valor de Entrada</span>}
                                        value={Number(cliente.valor_entrada || 0)}
                                        formatter={(val) => (
                                            <span className="font-extrabold text-foreground">
                                                {formatCurrency(Number(val))}
                                            </span>
                                        )}
                                    />
                                </AntCard>

                                <AntCard className="shadow-sm border-none bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden hover:ring-1 hover:ring-primary/10 transition-all">
                                    <Statistic
                                        title={<span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">Tipo do Bem</span>}
                                        value={cliente.tipo_bem || "N/A"}
                                        formatter={(val) => (
                                            <div className="flex items-center gap-2">
                                                <span className="font-extrabold text-foreground">{String(val)}</span>
                                            </div>
                                        )}
                                    />
                                </AntCard>

                                <AntCard className="shadow-sm border-none bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden hover:ring-1 hover:ring-primary/10 transition-all">
                                    <Statistic
                                        title={<span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">Prioridade</span>}
                                        value={cliente.prioridade || "MEDIA"}
                                        formatter={(val) => (
                                            <Tag color={val === 'ALTA' ? 'red' : val === 'MEDIA' ? 'blue' : 'default'} className="m-0 uppercase font-black px-2 pb-0.5 border-none shadow-sm text-[11px]">
                                                {PRIORITY_LABELS[String(val)] || String(val)}
                                            </Tag>
                                        )}
                                    />
                                </AntCard>

                                <AntCard className="shadow-sm border-none bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden hover:ring-1 hover:ring-primary/10 transition-all">
                                    <Statistic
                                        title={<span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">Temperatura</span>}
                                        value={cliente.temperatura_lead || "FRIO"}
                                        formatter={(val) => (
                                            <Tag color={val === 'QUENTE' ? 'volcano' : val === 'MEDIO' ? 'orange' : 'cyan'} className="m-0 uppercase font-black px-2 pb-0.5 border-none shadow-sm text-[11px]">
                                                <span className="flex items-center gap-1">
                                                    <TrendingUp className="h-3 w-3" />
                                                    {String(val)}
                                                </span>
                                            </Tag>
                                        )}
                                    />
                                </AntCard>
                            </div>

                            {/* ── TABS PRINCIPAIS ── */}
                            <div className="bg-white dark:bg-zinc-900 rounded-3xl shadow-xl shadow-blue-500/5 p-2 sm:p-6 border border-border/40">
                                <Tabs defaultActiveKey="geral" className="cliente-tabs" size="large">
                                    <TabPane
                                        tab={<span className="flex items-center gap-2"><UserCircle className="h-4 w-4" /> Dados Gerais</span>}
                                        key="geral"
                                    >
                                        <div className="space-y-8 pt-4">
                                            <section>
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
                                                        <Briefcase className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                                    </div>
                                                    <h3 className="text-lg font-bold tracking-tight">Informações Pessoais</h3>
                                                </div>
                                                <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small" className="bg-gray-50/30 rounded-lg overflow-hidden">
                                                    <Descriptions.Item label="CPF/CNPJ" span={1}>{formatCpfCnpj(cliente.cpf)}</Descriptions.Item>
                                                    <Descriptions.Item label="Email" span={2}>
                                                        <div className="flex items-center gap-2">
                                                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                                                            {cliente.email || 'N/A'}
                                                        </div>
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Telefone Principal">{formatPhone(cliente.tel1)}</Descriptions.Item>
                                                    <Descriptions.Item label="Telefone Secundário">{formatPhone(cliente.tel2)}</Descriptions.Item>
                                                    <Descriptions.Item label="Data de Nascimento">{formatDate(cliente.data_nascimento)}</Descriptions.Item>
                                                    <Descriptions.Item label="RG">{cliente.rg || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Órgão Emissor / Data">{cliente.data_emissao_rg ? formatDate(cliente.data_emissao_rg) : 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Estado Civil" className="capitalize">{cliente.estado_civil?.replace('_', ' ') || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Naturalidade">{cliente.naturalidade || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Nacionalidade">{cliente.nacionalidade || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Atividade">{cliente.atividade_desenvolvida || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Renda">{formatCurrency(cliente.rendimento_titular)}</Descriptions.Item>
                                                    <Descriptions.Item label="Nome da Mãe" span={3}>{cliente.nome_mae || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Nome do Pai" span={3}>{cliente.nome_pai || 'N/A'}</Descriptions.Item>
                                                </Descriptions>
                                            </section>

                                            <section>
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl">
                                                        <MapPin className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                                                    </div>
                                                    <h3 className="text-lg font-bold tracking-tight">Endereço de Residência</h3>
                                                </div>
                                                <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
                                                    <Descriptions.Item label="Logradouro" span={2}>{cliente.logradouro || 'N/A'}, {cliente.numero || 'S/N'}</Descriptions.Item>
                                                    <Descriptions.Item label="CEP">{cliente.cep || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Bairro">{cliente.bairro || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Cidade">{cliente.cidade || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Estado">{cliente.estado || 'N/A'}</Descriptions.Item>
                                                    <Descriptions.Item label="Complemento" span={3}>{cliente.complemento || 'N/A'}</Descriptions.Item>
                                                </Descriptions>
                                            </section>

                                            {cliente.banco && (
                                                <section>
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <div className="p-2 bg-amber-50 dark:bg-amber-900/30 rounded-xl">
                                                            <CreditCard className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                                                        </div>
                                                        <h3 className="text-lg font-bold tracking-tight">Dados Bancários</h3>
                                                    </div>
                                                    <Descriptions column={{ xs: 1, sm: 2, md: 4 }} bordered size="small">
                                                        <Descriptions.Item label="Banco" span={2}>{cliente.banco}</Descriptions.Item>
                                                        <Descriptions.Item label="Agência">{cliente.agencia}</Descriptions.Item>
                                                        <Descriptions.Item label="Conta">{cliente.conta}</Descriptions.Item>
                                                    </Descriptions>
                                                </section>
                                            )}
                                        </div>
                                    </TabPane>

                                    <TabPane
                                        tab={<span className="flex items-center gap-2"><Target className="h-4 w-4" /> Qualificação</span>}
                                        key="qualificacao"
                                    >
                                        <div className="space-y-6 pt-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <section>
                                                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-widest mb-4">Planejamento e Perfil</h3>
                                                    <Descriptions column={1} bordered size="small">
                                                        <Descriptions.Item label="Prioridade de Compra">
                                                            <Tag color={cliente.prioridade === 'ALTA' ? 'red' : 'blue'}>
                                                                {PRIORITY_LABELS[cliente.prioridade || ''] || 'N/A'}
                                                            </Tag>
                                                        </Descriptions.Item>
                                                        <Descriptions.Item label="Tempo de Procura">{cliente.tempo_procura ? formatTempoProcuraLabel(cliente.tempo_procura) : 'N/A'}</Descriptions.Item>
                                                        <Descriptions.Item label="Tempo de Aluguel">{cliente.tempo_aluguel || 'N/A'}</Descriptions.Item>
                                                        <Descriptions.Item label="Status Qualificação">
                                                            <Tag color={cliente.cliente_qualificado ? 'success' : 'default'} icon={<ShieldCheck className="h-3 w-3 inline mr-1" />}>
                                                                {cliente.cliente_qualificado ? 'QUALIFICADO' : 'EM ANÁLISE'}
                                                            </Tag>
                                                        </Descriptions.Item>
                                                    </Descriptions>
                                                </section>

                                                <section>
                                                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-widest mb-4">Interações e Status</h3>
                                                    <div className="bg-muted/30 rounded-2xl p-6 space-y-4">
                                                        <div className="flex justify-between items-center bg-white dark:bg-zinc-800 p-3 rounded-xl shadow-sm">
                                                            <span className="text-sm font-medium">Ligações Realizadas</span>
                                                            <AntBadge count={cliente.ligacoes_realizadas || 0} showZero color="#3b82f6" style={{ fontWeight: 800 }} />
                                                        </div>
                                                        {cliente.status_atual && (
                                                            <div className="space-y-2">
                                                                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Observação do Status</span>
                                                                <div className="bg-white dark:bg-zinc-800 p-4 rounded-xl border-l-4 border-primary shadow-sm">
                                                                    <p className="text-sm italic leading-relaxed">"{cliente.status_atual}"</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {cliente.observacoes && (
                                                            <div className="space-y-2">
                                                                <span className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Observações Gerais</span>
                                                                <p className="text-sm text-balance leading-relaxed text-muted-foreground">{cliente.observacoes}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </section>
                                            </div>
                                        </div>
                                    </TabPane>

                                    <TabPane
                                        tab={<span className="flex items-center gap-2"><FileText className="h-4 w-4" /> Contratos <Tag className="ml-1 rounded-full px-1.5 py-0 border-none bg-blue-100 text-blue-700">{cliente.contratos?.length || 0}</Tag></span>}
                                        key="contratos"
                                    >
                                        <div className="pt-4">
                                            {!cliente.contratos || cliente.contratos.length === 0 ? (
                                                <div className="text-center py-12 bg-muted/20 rounded-2xl border border-dashed">
                                                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-20" />
                                                    <p className="text-muted-foreground">Este cliente ainda não possui contratos vinculados.</p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 gap-4">
                                                    {cliente.contratos.map((contrato: any) => (
                                                        <div key={contrato.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-white dark:bg-zinc-800 rounded-2xl border border-border/60 hover:border-primary/40 transition-all shadow-sm group">
                                                            <div className="flex items-center gap-4">
                                                                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-xl">
                                                                    <FileText className="h-6 w-6 text-blue-600" />
                                                                </div>
                                                                <div>
                                                                    <div className="flex items-center gap-2 mb-0.5">
                                                                        <span className="text-xs font-bold text-muted-foreground">Contrato #{contrato.contrato_numero || contrato.id}</span>
                                                                        <Tag color={contrato.status === 'ativo' ? 'success' : 'processing'} className="m-0 text-[10px] uppercase font-bold border-none px-2 rounded-full">
                                                                            {contrato.status}
                                                                        </Tag>
                                                                    </div>
                                                                    <div className="text-lg font-black tracking-tight group-hover:text-primary transition-colors">
                                                                        {formatCurrency(contrato.bens?.preco_bem || 0)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="mt-4 sm:mt-0">
                                                                <a
                                                                    href={`/contratos/${contrato.id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-primary hover:text-white rounded-xl text-sm font-bold transition-all"
                                                                >
                                                                    Ver Detalhes <ExternalLink className="h-4 w-4" />
                                                                </a>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </TabPane>

                                    <TabPane
                                        tab={<span className="flex items-center gap-2"><Paperclip className="h-4 w-4" /> Documentos <Tag className="ml-1 rounded-full px-1.5 py-0 border-none bg-blue-100 text-blue-700">{documentos?.length || 0}</Tag></span>}
                                        key="documentos"
                                    >
                                        <div className="pt-4">
                                            {!documentos || documentos.length === 0 ? (
                                                <div className="text-center py-12 bg-muted/20 rounded-2xl border border-dashed">
                                                    <Paperclip className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-20" />
                                                    <p className="text-muted-foreground">Nenhum documento anexado para este cliente.</p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    {documentos.map((doc: any) => (
                                                        <div key={doc.id} className="flex items-center justify-between p-4 bg-white dark:bg-zinc-800 border border-border/60 rounded-2xl hover:shadow-lg hover:shadow-primary/5 transition-all group">
                                                            <div className="flex items-center gap-4 overflow-hidden">
                                                                <div className="bg-primary/5 p-3 rounded-xl group-hover:bg-primary/10 transition-colors">
                                                                    <File className="h-6 w-6 text-primary" />
                                                                </div>
                                                                <div className="flex flex-col min-w-0">
                                                                    <span className="text-sm font-bold truncate group-hover:text-primary transition-colors">
                                                                        {doc.nome_arquivo || doc.tipo || "Documento"}
                                                                    </span>
                                                                    <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest mt-0.5">
                                                                        {doc.tipo_documento || doc.tipo || "Anexo"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <AntTooltip title="Abrir Documento">
                                                                <a
                                                                    href={doc.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="p-3 bg-muted/50 hover:bg-primary hover:text-white rounded-xl transition-all"
                                                                >
                                                                    <ExternalLink className="h-5 w-5" />
                                                                </a>
                                                            </AntTooltip>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </TabPane>
                                </Tabs>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
